import { useMutation, useQuery } from "@apollo/client";
import { Route, Routes as RouterRoutes, useParams } from "react-router-dom";
import { useMemo } from "react";

import { FETCH_CONTACT as fetchContact } from "../../../../graphql/query/Contact";
import { UPDATE_CONTACT as updateContactMutation } from "../../../../graphql/mutation/Contact";
import AuthRoute from "../../../../components/AuthRoute";
import DetailBar from "./components/DetailBar";
import ActivityTracker from "./components/ActivityTracker";
import routes from "./routes";
import { FormSaveStatus, NavBar } from "../../../../components/ui";
import useAutosaveFormik from "../../../../hooks/useAutosaveFormik";
import { Portal, getChangedFields } from "../../../../util";

const Body = props => {
  return (
    <RouterRoutes>
      {routes
        ?.filter?.(item => item?.element)
        ?.map((item, index) => {
          return (
            <Route
              key={index}
              path={item?.href}
              element={
                <AuthRoute permissions={item.permissions}>
                  <item.element {...props} />
                </AuthRoute>
              }
            />
          );
        })}
    </RouterRoutes>
  );
};

const contactCollectionFields = ["contactArtists", "contactFocuses"];

const getContactFormValues = contact =>
  contact && {
    ...contact,
    contactArtists:
      contact.contactArtists?.map(item => ({
        label: item?.artist?.name,
        value: item?.artist?.id,
        contactArtistId: item?.id,
      })) || [],
    contactFocuses:
      contact.contactFocuses?.map(item => ({
        label: item?.focus?.description,
        value: item?.focus?.id,
        contactFocusId: item?.id,
      })) || [],
  };

const ContactForm = props => {
  const { id } = useParams();
  const { data: { contact } = {} } = useQuery(
    fetchContact({
      artistIDs: true,
      contactAddresses: { country: {} },
      contactArtists: { artist: {} },
      contactCompany: {},
      contactFocuses: { focus: {} },
      contactMailingLists: { mailingList: {} },
      contactNumbers: { type: {} },
      country: {},
      documents: {},
      exhUser: {},
      salesUser: {},
      salesUser2: {},
      type: {},
      type2: {},
      address: true,
      address2: true,
      address3: true,
      affiliations: true,
      biography: true,
      collection: true,
      collectionActive: true,
      company: true,
      confidential: true,
      contactSecondary: true,
      countryId: true,
      county: true,
      createdAt: true,
      dateEntered: true,
      details: true,
      dob: true,
      email: true,
      eori: true,
      exhUserId: true,
      firstcontactdate: true,
      firstcontactlocation: true,
      firstcontacttype: true,
      firstUserId: true,
      forename: true,
      greeting: true,
      img: true,
      invoicesLength: true,
      level: true,
      mailingname: true,
      middlename: true,
      name: true,
      notes: true,
      postcode: true,
      prefered: true,
      rating: true,
      sagecode: true,
      salesUserId: true,
      salesUser2Id: true,
      salutation: true,
      siteId: true,
      storageCharge: true,
      surname: true,
      title: true,
      totalSpend: true,
      town: true,
      typeId: true,
      type2Id: true,
      unsubscribed: true,
      updatedAt: true,
      userEntered: true,
      user: true,
      vat: true,
    }),
    {
      variables: { id: +id },
    },
  );
  const activityTrackerProps = {
    contact,
  };

  const [updateContact, { loading }] = useMutation(
    updateContactMutation({
      contactAddresses: {},
      contactArtists: { artist: true },
      contactCompany: {},
      contactFocuses: { focus: true },
      contactMailingLists: { mailingList: {} },
      contactNumbers: { type: {} },
      country: {},
      exhUser: {},
      salesUser: {},
      salesUser2: {},
      type: {},
      type2: {},
    }),
  );

  const saveContactChanges = async (values, savedValues) => {
    const changedFields = getChangedFields(values, savedValues);
    const filteredValues = Object.keys(changedFields).reduce((acc, key) => {
      if (!contactCollectionFields.includes(key)) {
        acc[key] = changedFields[key];
      }
      return acc;
    }, {});

    if (Object.keys(filteredValues).length > 0) {
      const { data } = await updateContact({
        variables: {
          input: {
            id: +id,
            ...filteredValues,
          },
        },
      });

      if (!data?.updateContact?.success) {
        throw new Error(
          data?.updateContact?.error || "The contact could not be saved",
        );
      }
    }
  };

  const updateContactValue = values => saveContactChanges(values, contact);

  const contactFormValues = useMemo(
    () => getContactFormValues(contact),
    [contact],
  );

  const formik = useAutosaveFormik({
    externallySavedFields: contactCollectionFields,
    initialValues: contactFormValues,
    onSave: saveContactChanges,
  });

  const bodyProps = {
    ...props,
    contact,
    contactId: +id,
    formik,
    loading,
    updateContact,
    updateContactValue,
  };

  return (
    <div className="relative flex min-h-full flex-col lg:flex-row">
      <Portal id="form-save-status">
        <FormSaveStatus state={formik.autosave.formState} />
      </Portal>
      <Portal id="sidebar">
        <NavBar
          navigation={routes?.filter?.(item => item?.icon)}
          open={props?.open}
        />
      </Portal>
      <DetailBar {...bodyProps} />
      <div className="m-4 flex min-w-0 flex-1 overflow-x-auto lg:m-8 lg:mr-[80px]">
        {contact && <Body {...bodyProps} />}
      </div>
      <ActivityTracker {...activityTrackerProps} />
    </div>
  );
};

const Wizard = props => {
  const { id } = useParams();

  return <ContactForm key={id} {...props} />;
};

export default Wizard;
